import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink, Redirect, useHistory, useParams } from 'react-router-dom';
import {
	Alert,
	Box,
	Button,
	Chip,
	CircularProgress,
	Container,
	Divider,
	FormControl,
	FormControlLabel,
	Grid,
	InputAdornment,
	InputLabel,
	LinearProgress,
	MenuItem,
	Paper,
	Select,
	Skeleton,
	Stack,
	Switch,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	ToggleButton,
	ToggleButtonGroup,
	Tooltip,
	Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { shallowEqual, useSelector } from 'react-redux';
import { RootState } from 'store';
import { Layout } from 'features/common/components/Layout';
import { useCurrentUserTeam } from 'features/auth/hooks/useCurrentUserTeam';
import { isAdminOrSuperAdmin, isSuperAdmin } from 'features/auth/helpers';
import { SALES_TEAMS } from 'utils/slug';
import routes from 'constants/routes';
import { KpiCard } from 'features/salesDashboard/components/KpiCard';
import { AgeIndicator } from 'features/salesDashboard/components/AgeIndicator';
import { CachedBadge } from 'features/salesDashboard/components/CachedBadge';
import { MonthlySeriesChart } from 'features/salesDashboard/components/MonthlySeriesChart';
import { QuickLinksBar } from 'features/salesDashboard/components/QuickLinksBar';
import { useGoalAttainment } from 'features/salesDashboard/hooks/useGoalAttainment';
import { useSummaryCard } from 'features/salesDashboard/hooks/useSummaryCard';
import { useMonthlySeries } from 'features/salesDashboard/hooks/useMonthlySeries';
import { useConversion } from 'features/salesDashboard/hooks/useConversion';
import { useConversionByTeam } from 'features/salesDashboard/hooks/useConversionByTeam';
import { usePendingConversion } from 'features/salesDashboard/hooks/usePendingConversion';
import { useAlerts } from 'features/salesDashboard/hooks/useAlerts';
import { useUpcomingTravels } from 'features/salesDashboard/hooks/useUpcomingTravels';
import { useLeaderboard } from 'features/salesDashboard/hooks/useLeaderboard';
import { useMonthlySeriesByTeam } from 'features/salesDashboard/hooks/useMonthlySeriesByTeam';
import { useProductionMix } from 'features/salesDashboard/hooks/useProductionMix';
import { useOperationalDashboard } from 'features/salesDashboard/hooks/useOperationalDashboard';
import { usePendingBalance } from 'features/salesDashboard/hooks/usePendingBalance';
import { usePendingBalanceSummary } from 'features/salesDashboard/hooks/usePendingBalanceSummary';
import { useVariations } from 'features/salesDashboard/hooks/useVariations';
import { slugify, teamFromSlug, isSalesTeam } from 'utils/slug';
import { serviceTypeToEmoji } from 'features/salesOrder/constants';
import { SectionInfoTooltip } from 'features/salesDashboard/components/SectionInfoTooltip';
import { BranchCashBox } from 'features/salesDashboard/components/BranchCashBox';

// ─── Utilidades ───────────────────────────────────────────────────────────────

function formatUSD(value: number, decimals = 0): string {
	return (
		'USD ' +
		new Intl.NumberFormat('es-AR', {
			style: 'decimal',
			minimumFractionDigits: decimals,
			maximumFractionDigits: decimals,
		}).format(value)
	);
}

function formatDate(isoString: string): string {
	return new Date(isoString).toLocaleDateString('es', { day: '2-digit', month: '2-digit', year: '2-digit' });
}

function getDefaultDateRange(): { from: string; to: string } {
	const now = new Date();
	const from = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
	const tomorrow = new Date(now);
	tomorrow.setDate(tomorrow.getDate() + 1);
	const to = tomorrow.toISOString().split('T')[0];
	return { from, to };
}

// ─── Scope types ──────────────────────────────────────────────────────────────

interface SellerScope {
	type: 'seller';
	sellerId: number;
	sellerName: string;
	/** Equipo al que pertenece este vendedor (para navegación de admin/superadmin). */
	contextTeam?: string;
}

interface TeamScope {
	type: 'team';
	team: string; // nombre canónico ej. "WTC UY"
}

type DashboardScope = SellerScope | TeamScope;

// ─── Lazy loading: dispara la carga cuando el sentinel entra al viewport ──────
// Una vez visible, queda "congelado" en true (no se vuelve a observar) para que
// las secciones ya cargadas no se desmonten al hacer scroll hacia arriba.
function useInView<T extends HTMLElement>(): [React.RefObject<T>, boolean] {
	const ref = useRef<T>(null);
	const [inView, setInView] = useState(false);

	useEffect(() => {
		const el = ref.current;
		if (!el || inView) return;
		if (typeof IntersectionObserver === 'undefined') {
			// Entorno sin soporte (ej. tests jsdom) → cargar de una
			setInView(true);
			return;
		}
		const observer = new IntersectionObserver(
			(entries) => {
				if (entries.some((e) => e.isIntersecting)) setInView(true);
			},
			{ rootMargin: '250px' }, // precarga un poco antes de llegar
		);
		observer.observe(el);
		return () => observer.disconnect();
	}, [inView]);

	return [ref, inView];
}

// ─── Componente principal ─────────────────────────────────────────────────────

interface SalesDashboardProps {
	scope: DashboardScope;
}

// ─── Widget: Necesidades operativas abiertas ──────────────────────────────────

const RESPONSABLE_COLOR: Record<string, 'warning' | 'error' | 'info'> = {
	equipo: 'warning',
	cliente: 'error',
	'en curso': 'info',
};

function stripWorkflowPrefix(name: string): string {
	return name.replace(/^\[.*?\]\s*/, '').trim();
}

function OperationalNeedsWidget({
	sellerId,
	team,
	enabled,
}: {
	sellerId?: number;
	team?: string;
	enabled: boolean;
}): JSX.Element | null {
	const { data, loading, error } = useOperationalDashboard({ sellerId, team, enabled });
	const [workflowFilter, setWorkflowFilter] = useState<string | null>(null);

	const operationalNeedsTooltip = (
		<>
			<Typography variant="body2" fontWeight={600} gutterBottom>
				¿Qué hay acá? 👀
			</Typography>
			<Typography variant="body2" gutterBottom>
				Las gestiones operativas abiertas que se pidieron a operaciones 🛠️ (cobros contra cía, devoluciones, ajustes,
				etc.). Ojo: esto NO son ventas — son trámites en curso.
			</Typography>
			<Typography variant="body2" fontWeight={600} gutterBottom>
				Responsable te dice de quién depende el próximo paso 🎾:
			</Typography>
			<Typography variant="body2" component="ul" sx={{ pl: 2, mb: 1 }}>
				<li>
					🧑‍💼 <strong>equipo</strong> → la pelota la tiene operaciones, les toca mover.
				</li>
				<li>
					🙋 <strong>cliente</strong> → esperando respuesta de la contraparte.
				</li>
				<li>
					⏳ <strong>en curso</strong> → se está laburando ahora.
				</li>
			</Typography>
			<Typography variant="body2">
				Lo resuelto ✅ y lo cancelado ❌ no se muestra — solo lo que sigue abierto.
			</Typography>
		</>
	);

	if (!enabled) return null;

	if (loading) {
		return (
			<>
				<Divider sx={{ mb: 3 }} />
				<Skeleton variant="rectangular" height={80} sx={{ mb: 3 }} />
			</>
		);
	}

	if (error) {
		return (
			<>
				<Divider sx={{ mb: 3 }} />
				<Alert severity="error" sx={{ mb: 3 }}>
					{error}
				</Alert>
			</>
		);
	}

	// Empty-state: sin workflows configurados o sin casos
	if (!data || data.workflowIds.length === 0 || data.cases.length === 0) {
		return (
			<>
				<Divider sx={{ mb: 3 }} />
				<Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
					<Box display="inline-flex" alignItems="center" gap={0.5}>
						<Typography variant="subtitle1" fontWeight={600}>
							Necesidades operativas abiertas
						</Typography>
						<SectionInfoTooltip content={operationalNeedsTooltip} />
					</Box>
				</Box>
				<Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
					Sin necesidades operativas abiertas.
				</Typography>
			</>
		);
	}

	const { summary, cases } = data;

	return (
		<>
			<Divider sx={{ mb: 3 }} />
			<Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
				<Box display="inline-flex" alignItems="center" gap={0.5}>
					<Typography variant="subtitle1" fontWeight={600}>
						Necesidades operativas abiertas
					</Typography>
					<SectionInfoTooltip content={operationalNeedsTooltip} />
				</Box>
				<Chip label={`${summary.total} caso${summary.total !== 1 ? 's' : ''}`} size="small" color="warning" />
			</Box>

			{/* Chips por responsable */}
			<Stack direction="row" spacing={1} flexWrap="wrap" mb={2}>
				{(Object.entries(summary.by_responsable) as [string, number][])
					.filter(([, n]) => n > 0)
					.map(([resp, n]) => (
						<Chip key={resp} label={`${resp}: ${n}`} size="small" color={RESPONSABLE_COLOR[resp] ?? 'default'} />
					))}
			</Stack>

			{/* Chips de filtro por workflow */}
			{(() => {
				const workflows = Array.from(new Set(cases.map((c) => stripWorkflowPrefix(c.workflow_name ?? ''))))
					.filter(Boolean)
					.sort();
				if (workflows.length <= 1) return null;
				return (
					<Box display="flex" gap={0.75} flexWrap="wrap" mb={1.5}>
						<Chip
							label="Todos"
							size="small"
							color={workflowFilter === null ? 'primary' : 'default'}
							variant={workflowFilter === null ? 'filled' : 'outlined'}
							onClick={() => setWorkflowFilter(null)}
							sx={{ cursor: 'pointer' }}
						/>
						{workflows.map((wf) => (
							<Chip
								key={wf}
								label={wf}
								size="small"
								color={workflowFilter === wf ? 'primary' : 'default'}
								variant={workflowFilter === wf ? 'filled' : 'outlined'}
								onClick={() => setWorkflowFilter(workflowFilter === wf ? null : wf)}
								sx={{ cursor: 'pointer' }}
							/>
						))}
					</Box>
				);
			})()}

			{/* Lista de casos */}
			<TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
				<Table size="small">
					<TableHead>
						<TableRow>
							<TableCell>Caso</TableCell>
							<TableCell>Categoría</TableCell>
							<TableCell>Step</TableCell>
							<TableCell>Responsable</TableCell>
							<TableCell>Fechas</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{cases
							.filter((c) =>
								workflowFilter === null ? true : stripWorkflowPrefix(c.workflow_name ?? '') === workflowFilter,
							)
							.map((c) => (
								<TableRow key={c.case_id} hover>
									<TableCell sx={{ maxWidth: 220 }}>
										<Typography variant="body2" fontWeight={500} noWrap title={c.name}>
											{c.name}
										</Typography>
										<Box
											component="a"
											href={`https://crm.mevuelo.com/dashboard/cases/${c.case_id}`}
											target="_blank"
											rel="noopener noreferrer"
											sx={{
												display: 'inline-flex',
												alignItems: 'center',
												gap: 0.25,
												fontSize: '0.65rem',
												fontFamily: 'monospace',
												color: 'text.disabled',
												textDecoration: 'none',
												'&:hover': { color: 'primary.main', textDecoration: 'underline' },
											}}
										>
											{c.case_id}
											<OpenInNewIcon sx={{ fontSize: 10 }} />
										</Box>
									</TableCell>
									<TableCell>
										<Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap">
											{c.area && <Chip label={c.area} size="small" variant="outlined" />}
											<Typography variant="caption" color="text.secondary">
												{c.workflow_name}
											</Typography>
										</Stack>
									</TableCell>
									<TableCell>
										<Typography variant="body2">{c.step_name}</Typography>
									</TableCell>
									<TableCell>
										{c.responsable ? (
											<Chip label={c.responsable} size="small" color={RESPONSABLE_COLOR[c.responsable] ?? 'default'} />
										) : (
											<Typography variant="caption" color="text.disabled">
												—
											</Typography>
										)}
									</TableCell>
									<TableCell sx={{ whiteSpace: 'nowrap' }}>
										<Typography variant="caption" color="text.secondary" display="block">
											<Box component="span" sx={{ fontWeight: 600, mr: 0.5 }}>
												Creado
											</Box>
											{new Date(c.created_at).toLocaleString('es', {
												day: '2-digit',
												month: '2-digit',
												year: '2-digit',
												hour: '2-digit',
												minute: '2-digit',
											})}
										</Typography>
										<Typography variant="caption" color="text.disabled" display="block">
											<Box component="span" sx={{ fontWeight: 600, mr: 0.5 }}>
												Upd.
											</Box>
											{new Date(c.updated_at).toLocaleString('es', {
												day: '2-digit',
												month: '2-digit',
												year: '2-digit',
												hour: '2-digit',
												minute: '2-digit',
											})}
										</Typography>
									</TableCell>
								</TableRow>
							))}
					</TableBody>
				</Table>
			</TableContainer>
		</>
	);
}

export function SalesDashboard({ scope }: SalesDashboardProps): JSX.Element {
	const history = useHistory();
	const defaults = getDefaultDateRange();
	const [dateFrom, setDateFrom] = useState(defaults.from);
	const [dateTo, setDateTo] = useState(defaults.to);
	const [projectionMode, setProjectionMode] = useState<'weekdays' | 'linear'>('weekdays');
	const [onlyNegative, setOnlyNegative] = useState(true);
	const [threshold, setThreshold] = useState(30);
	const [pendingBucket, setPendingBucket] = useState<string | undefined>(undefined);
	const [pendingOffset, setPendingOffset] = useState(0);
	const [balanceSegmentFilter, setBalanceSegmentFilter] = useState<string | null>(null);

	const isTeam = scope.type === 'team';
	const scopeTeam = isTeam ? (scope as TeamScope).team : undefined;
	const scopeSellerId = !isTeam ? (scope as SellerScope).sellerId : undefined;
	const scopeSellerName = !isTeam ? (scope as SellerScope).sellerName : undefined;
	// contextTeam: en vista equipo = ese equipo; en vista vendedor = equipo del vendedor
	const contextTeam = isTeam ? scopeTeam : (scope as SellerScope).contextTeam;

	const user = useSelector((state: RootState) => state.auth.user, shallowEqual);
	const canNavigate = isAdminOrSuperAdmin(user);

	// Lazy loading: las secciones below-the-fold solo disparan su fetch cuando el
	// usuario hace scroll hasta el sentinel. Así la carga inicial es liviana y el
	// usuario puede interactuar de inmediato con la parte superior.
	const [belowFoldRef, belowFoldInView] = useInView<HTMLDivElement>();

	// ── Data hooks ────────────────────────────────────────────────────────────
	// Para superadmin sin contextTeam, cargamos el leaderboard global para deducir
	// el equipo del vendedor actual. Para el resto, filtramos por equipo.
	const needsGlobalLookup = canNavigate && !contextTeam && !isTeam;
	const leaderboardTeam = needsGlobalLookup ? undefined : contextTeam ?? scopeTeam;
	const leaderboard = useLeaderboard({ team: leaderboardTeam });

	// Equipo resuelto: si había contextTeam lo usamos directo; si no, lo deducimos
	// del leaderboard global buscando al vendedor actual por ID.
	const resolvedContextTeam = useMemo(() => {
		if (contextTeam) return contextTeam;
		if (!needsGlobalLookup || !scopeSellerId || !leaderboard.data) return undefined;
		return leaderboard.data.find((s) => Number(s.seller_id) === scopeSellerId)?.team;
	}, [contextTeam, needsGlobalLookup, scopeSellerId, leaderboard.data]);

	// Para el select de vendedor en superadmin sin contextTeam, una vez que resolvemos
	// el equipo del vendedor filtramos el leaderboard a ese equipo (evita duplicados).
	const sellerSelectData = useMemo(() => {
		if (!leaderboard.data) return null;
		if (resolvedContextTeam && needsGlobalLookup) {
			return leaderboard.data.filter((s) => s.team === resolvedContextTeam);
		}
		return leaderboard.data;
	}, [leaderboard.data, resolvedContextTeam, needsGlobalLookup]);

	// ¿El vendedor en scope está entre las opciones del select? (evita value out-of-range)
	const sellerInList = useMemo(
		() => !isTeam && !!sellerSelectData?.some((s) => String(s.seller_id) === String(scopeSellerId)),
		[isTeam, sellerSelectData, scopeSellerId],
	);

	const goalAttainment = useGoalAttainment({ team: scopeTeam });
	const summaryCard = useSummaryCard({
		team: isTeam ? scopeTeam : undefined,
		sellerId: !isTeam ? scopeSellerId : undefined,
	});
	const monthlySeries = useMonthlySeries({ sellerId: scopeSellerId });
	const monthlySeriesByTeam = useMonthlySeriesByTeam({ team: scopeTeam });
	const conversion = useConversion(dateFrom, dateTo, { sellerId: scopeSellerId, team: scopeTeam });
	const conversionByTeam = useConversionByTeam(dateFrom, dateTo, { team: scopeTeam });
	const pendingConversion = usePendingConversion({
		sellerId: scopeSellerId,
		team: scopeTeam,
		bucket: pendingBucket,
		limit: 50,
		offset: pendingOffset,
	});
	// Secciones below-the-fold: gateadas por belowFoldInView (lazy load al scrollear)
	// Para el mix no pasamos `team`: el endpoint devuelve items sin segmentType cuando
	// se filtra solo por equipo. Con sellerId muestra el mix del vendedor; sin ningún
	// filtro muestra el mix global de la empresa (igualmente útil en vista equipo).
	const productionMix = useProductionMix(dateFrom, dateTo, {
		sellerId: scopeSellerId,
		enabled: belowFoldInView,
	});
	const pendingBalanceSummary = usePendingBalanceSummary({ team: scopeTeam, enabled: belowFoldInView });
	const pendingBalance = usePendingBalance({ team: scopeTeam, sellerId: scopeSellerId, enabled: belowFoldInView });
	const { criticalAlerts, pendingIssues } = useAlerts({
		sellerId: scopeSellerId,
		team: scopeTeam,
		enabled: belowFoldInView,
	});
	const upcomingTravels = useUpcomingTravels({
		sellerId: scopeSellerId,
		team: scopeTeam,
		weeks: 2,
		enabled: belowFoldInView,
	});
	const variations = useVariations(dateFrom, dateTo, {
		sellerId: scopeSellerId,
		team: scopeTeam,
		onlyNegative,
		threshold: onlyNegative ? threshold : undefined,
		enabled: belowFoldInView,
	});

	// ── Derived ───────────────────────────────────────────────────────────────
	const teamGoal = goalAttainment.teamTotals?.[0];
	const myGoal = !isTeam
		? goalAttainment.data?.find((g) => Number(g.seller_id) === scopeSellerId) ?? goalAttainment.data?.[0]
		: null;
	const card = summaryCard.data?.[0] ?? null;
	const conv = isTeam ? conversionByTeam.data?.[0] : conversion.data?.[0];
	const convLoading = isTeam ? conversionByTeam.loading : conversion.loading;
	const convCachedAt = isTeam ? conversionByTeam.cachedAt : conversion.cachedAt;
	const convError = isTeam ? conversionByTeam.error : conversion.error;

	const pendingCases = pendingConversion.data?.cases ?? [];
	const pendingBucketsSummary = pendingConversion.data?.buckets ?? [];
	const pendingPagination = pendingConversion.data?.pagination ?? null;
	const balanceSummary = pendingBalanceSummary.data?.[0] ?? null;

	const teamSeriesData = useMemo(() => {
		if (!monthlySeriesByTeam.data) return null;
		return monthlySeriesByTeam.data.map((item) => ({
			seller_id: 'team',
			seller_name: 'Equipo',
			month: item.month,
			sale_amount: Number(item.sale_amount),
			gross_profit: Number(item.gross_profit),
			sale_mom_abs: item.sale_mom_abs,
			sale_mom_pct: item.sale_mom_pct,
			gp_mom_pct: item.gp_mom_pct,
			sale_yoy_pct: item.sale_yoy_pct,
			gp_yoy_pct: item.gp_yoy_pct,
		}));
	}, [monthlySeriesByTeam.data]);

	const segmentColor: Record<string, string> = {
		Leisure: '#31194b',
		Corpo: '#ed6602',
		Corporate: '#ed6602',
		Collaborators: '#5c85d6',
		Other: '#aaa',
	};
	// Para vista de equipo, filtramos por los seller_id del equipo (del leaderboard)
	// y luego agrupamos client-side por segmento+producto. Así evitamos incluir
	// segmentos de otros equipos que no corresponden al equipo actual.
	const displayMixData = useMemo(() => {
		if (!productionMix.data) return null;
		if (!isTeam) return [...productionMix.data].sort((a, b) => Number(b.gross_profit) - Number(a.gross_profit));

		// Seller IDs del equipo actual (ya filtrado por equipo en useLeaderboard)
		const teamSellerIds = new Set((leaderboard.data ?? []).map((s) => String(s.seller_id)));

		const filtered =
			teamSellerIds.size > 0
				? productionMix.data.filter((item) => !item.seller_id || teamSellerIds.has(String(item.seller_id)))
				: productionMix.data;

		const map = new Map<
			string,
			{ segmentType: string; serviceType: string; sale_amount: number; gross_profit: number }
		>();
		filtered.forEach((item) => {
			const key = `${item.segmentType ?? ''}|${item.serviceType ?? ''}`;
			const existing = map.get(key);
			if (existing) {
				existing.sale_amount += Number(item.sale_amount);
				existing.gross_profit += Number(item.gross_profit);
			} else {
				map.set(key, {
					segmentType: item.segmentType ?? '',
					serviceType: item.serviceType ?? '',
					sale_amount: Number(item.sale_amount),
					gross_profit: Number(item.gross_profit),
				});
			}
		});
		return Array.from(map.values()).sort((a, b) => b.gross_profit - a.gross_profit);
	}, [isTeam, productionMix.data, leaderboard.data]);

	const mixTotal = useMemo(
		() => (displayMixData ?? []).reduce((s, i) => s + Number(i.sale_amount), 0),
		[displayMixData],
	);

	const sortedTravels = useMemo(() => {
		if (!upcomingTravels.data) return [];
		return [...upcomingTravels.data].sort(
			(a, b) => new Date(a.travelDepartureDate).getTime() - new Date(b.travelDepartureDate).getTime(),
		);
	}, [upcomingTravels.data]);

	const currentGoal = isTeam ? teamGoal : myGoal;
	const goalLoading = goalAttainment.loading;

	// ─── Render ────────────────────────────────────────────────────────────────
	return (
		<Layout>
			<Container maxWidth="xl" sx={{ py: 3 }}>
				{/* ── Quick Links ──────────────────────────────────── */}
				<QuickLinksBar />

				{/* ── Título + navegación ──────────────────────────── */}
				<Box mb={3} display="flex" alignItems="center" justifyContent="space-between" gap={2} flexWrap="wrap">
					{/* Izquierda: botón atrás + título */}
					<Box display="flex" alignItems="center" gap={1.5} flexShrink={0}>
						{!isTeam && canNavigate && resolvedContextTeam && (
							<Button
								size="small"
								variant="outlined"
								startIcon={<ArrowBackIcon sx={{ fontSize: 15 }} />}
								onClick={() => history.push(routes.salesDashboard.team(resolvedContextTeam))}
								sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
							>
								{resolvedContextTeam}
							</Button>
						)}
						<Typography variant="h5" fontWeight={700} color="primary">
							{isTeam ? `Equipo: ${(scope as TeamScope).team}` : `${scopeSellerName ?? '—'}`}
						</Typography>
					</Box>

					{/* Derecha: controles de navegación */}
					{canNavigate && (
						<Box display="flex" alignItems="center" gap={1.5}>
							{/* Select de equipo — solo superadmin */}
							{isSuperAdmin(user) && (
								<FormControl size="small" sx={{ minWidth: 150 }}>
									<InputLabel id="dash-team-select-label" shrink>
										Equipo
									</InputLabel>
									<Select
										labelId="dash-team-select-label"
										label="Equipo"
										displayEmpty
										// Solo pasamos el valor si es un equipo de ventas válido; si no
										// (ej. DIRECCION) dejamos '' para evitar el warning out-of-range de MUI.
										value={isSalesTeam(resolvedContextTeam) ? resolvedContextTeam : ''}
										renderValue={(val) => (val ? String(val) : <em style={{ color: '#888' }}>Elegí equipo…</em>)}
										onChange={(e) => {
											if (e.target.value) history.push(routes.salesDashboard.team(e.target.value));
										}}
									>
										{SALES_TEAMS.map((t) => (
											<MenuItem key={t} value={t}>
												{t}
											</MenuItem>
										))}
									</Select>
								</FormControl>
							)}

							{/* Select de vendedor — admin y superadmin */}
							{sellerSelectData && sellerSelectData.length > 0 && (
								<FormControl size="small" sx={{ minWidth: 190 }}>
									<InputLabel id="dash-seller-select-label">Vendedor</InputLabel>
									<Select
										labelId="dash-seller-select-label"
										label="Vendedor"
										// Solo usamos el id como value si está entre las opciones; si no
										// (ej. superadmin viéndose a sí mismo, sin ser vendedor del equipo)
										// dejamos '' para evitar el warning out-of-range de MUI.
										value={sellerInList ? String(scopeSellerId ?? '') : ''}
										onChange={(e) => {
											if (!e.target.value) {
												if (isSalesTeam(resolvedContextTeam))
													history.push(routes.salesDashboard.team(resolvedContextTeam));
											} else {
												const seller = sellerSelectData?.find((s) => String(s.seller_id) === e.target.value);
												if (seller) history.push(routes.salesDashboard.seller(seller.seller_name));
											}
										}}
										renderValue={(val) => {
											if (!val) return <em style={{ color: '#888' }}>Ver vendedor…</em>;
											const seller = sellerSelectData?.find((s) => String(s.seller_id) === val);
											return seller?.seller_name ?? val;
										}}
									>
										{!isTeam && isSalesTeam(resolvedContextTeam) && (
											<MenuItem value="">
												<em>← Ver equipo</em>
											</MenuItem>
										)}
										{sellerSelectData.map((s, idx) => (
											<MenuItem key={`${idx}-${s.seller_id}`} value={String(s.seller_id)}>
												{s.seller_name}
											</MenuItem>
										))}
									</Select>
								</FormControl>
							)}
						</Box>
					)}
				</Box>

				{/* ── Objetivo mensual ─────────────────────────────── */}
				{goalLoading ? (
					<Skeleton width="100%" height={30} sx={{ mb: 2 }} />
				) : currentGoal ? (
					<Box mb={3}>
						<Box display="flex" alignItems="center" gap={1} mb={0.5} flexWrap="wrap">
							<Box display="inline-flex" alignItems="center" gap={0.5} sx={{ mr: 'auto' }}>
								<Typography variant="body2" color="text.secondary">
									{isTeam ? 'Objetivo mensual del equipo' : 'Objetivo mensual'}:{' '}
									{Number(currentGoal.goal_attainment_pct ?? 0).toFixed(1)}% alcanzado (proyección:{' '}
									{projectionMode === 'weekdays'
										? Number(currentGoal.projected_attainment_pct_weekdays ?? 0).toFixed(1)
										: Number(currentGoal.projected_attainment_pct ?? 0).toFixed(1)}
									%)
								</Typography>
								<SectionInfoTooltip
									content={
										<>
											<Typography variant="body2" fontWeight={600} gutterBottom>
												¿Qué hay acá? 👀
											</Typography>
											<Typography variant="body2" gutterBottom>
												Cuánto del objetivo del mes ya está en el bolsillo 💪 y cuánto falta.
											</Typography>
											<Typography variant="body2" fontWeight={600} gutterBottom>
												La proyección 🔮 estima dónde vas a terminar si seguís a este ritmo:
											</Typography>
											<Typography variant="body2" component="ul" sx={{ pl: 2, mb: 1 }}>
												<li>
													📅 <strong>Días háb.</strong> → proyecta usando solo días hábiles (más realista si no vendés
													fines de semana).
												</li>
												<li>
													📏 <strong>Lineal</strong> → reparte el mes parejo, día por día.
												</li>
											</Typography>
											<Typography variant="body2">Barra verde = objetivo cumplido 🏆 ¡A por el siguiente!</Typography>
										</>
									}
								/>
							</Box>
							<ToggleButtonGroup
								value={projectionMode}
								exclusive
								onChange={(_, v) => {
									if (v) setProjectionMode(v);
								}}
								size="small"
							>
								<ToggleButton value="weekdays" sx={{ fontSize: 10, py: 0.25 }}>
									Días háb.
								</ToggleButton>
								<ToggleButton value="linear" sx={{ fontSize: 10, py: 0.25 }}>
									Lineal
								</ToggleButton>
							</ToggleButtonGroup>
							<CachedBadge cachedAt={goalAttainment.cachedAt} />
						</Box>
						<LinearProgress
							variant="determinate"
							value={Math.min(Number(currentGoal.goal_attainment_pct ?? 0), 100)}
							sx={{ height: 10, borderRadius: 5 }}
							color={Number(currentGoal.goal_attainment_pct ?? 0) >= 100 ? 'success' : 'primary'}
						/>
					</Box>
				) : null}

				{/* ── KPI Cards ────────────────────────────────────── */}
				{summaryCard.error && (
					<Alert severity="error" sx={{ mb: 2 }}>
						{summaryCard.error}
					</Alert>
				)}
				<Grid container spacing={2} mb={3}>
					{[
						{
							label: 'Venta hoy',
							value: card ? formatUSD(Number(card.sale_today)) : null,
							tooltip: 'Total vendido hoy 💸 (se actualiza durante el día).',
						},
						{
							label: 'Margen hoy',
							value: card ? formatUSD(Number(card.gp_today)) : null,
							tooltip: 'La ganancia bruta de lo vendido hoy 🤑',
						},
						{
							label: 'Venta MTD',
							value: card ? formatUSD(Number(card.sale_mtd)) : null,
							tooltip: 'Todo lo vendido desde el 1° del mes hasta hoy 📆',
						},
						{
							label: 'Margen MTD',
							value: card ? formatUSD(Number(card.gp_mtd)) : null,
							tooltip: 'Ganancia bruta acumulada del mes 📈',
						},
						{
							label: 'Venta 3M',
							value: card ? formatUSD(Number(card.sale_3m)) : null,
							tooltip: 'Todo lo vendido en los últimos 3 meses — tu tendencia de fondo 🌊',
						},
						{
							label: 'Margen 3M',
							value: card ? formatUSD(Number(card.gp_3m)) : null,
							tooltip: 'Ganancia bruta acumulada de los últimos 3 meses 🌊',
						},
					].map((c) => (
						<Grid item xs={6} sm={4} md={2} key={c.label}>
							<KpiCard label={c.label} value={c.value} loading={summaryCard.loading} tooltip={c.tooltip} />
						</Grid>
					))}
				</Grid>

				{/* ── Caja de sucursal (solo vista equipo) ─────────── */}
				{isTeam && scopeTeam && <BranchCashBox team={scopeTeam} />}

				{/* ── Evolución mensual ────────────────────────────── */}
				<Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
					<Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
						<Box display="inline-flex" alignItems="center" gap={0.5}>
							<Typography variant="subtitle1" fontWeight={600}>
								{isTeam ? 'Evolución mensual del equipo' : 'Evolución mensual'}
							</Typography>
							<SectionInfoTooltip
								content={
									<>
										<Typography variant="body2" fontWeight={600} gutterBottom>
											¿Qué hay acá? 👀
										</Typography>
										<Typography variant="body2" gutterBottom>
											Tu película mes a mes 🎬: venta y margen de los últimos meses para ver si venís subiendo 📈 o hay
											que meterle pata 🏃.
										</Typography>
										<Typography variant="body2">
											Ideal para detectar temporadas fuertes 🏖️ y meses flojos antes de que te sorprendan.
										</Typography>
									</>
								}
							/>
						</Box>
						<CachedBadge cachedAt={isTeam ? monthlySeriesByTeam.cachedAt : monthlySeries.cachedAt} />
					</Box>
					{isTeam
						? monthlySeriesByTeam.error && <Alert severity="error">{monthlySeriesByTeam.error}</Alert>
						: monthlySeries.error && <Alert severity="error">{monthlySeries.error}</Alert>}
					<MonthlySeriesChart
						data={isTeam ? teamSeriesData : monthlySeries.data}
						loading={isTeam ? monthlySeriesByTeam.loading : monthlySeries.loading}
						sellerId={isTeam ? 'team' : scopeSellerId}
					/>
				</Paper>

				{/* ── Conversión ───────────────────────────────────── */}
				<Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1.5} flexWrap="wrap" gap={1}>
					<Box display="inline-flex" alignItems="center" gap={0.5}>
						<Typography variant="subtitle1" fontWeight={600}>
							{isTeam ? 'Conversión del equipo' : 'Conversión (período seleccionado)'}
						</Typography>
						<SectionInfoTooltip
							content={
								<>
									<Typography variant="body2" fontWeight={600} gutterBottom>
										¿Qué hay acá? 👀
									</Typography>
									<Typography variant="body2" gutterBottom>
										De todos los casos que te asignaron en el período, cuántos convertiste en venta 🎯.
									</Typography>
									<Typography variant="body2">
										Podés mover las fechas 📆 para mirar el período que quieras. Cada tarjetita tiene su propia ayuda
										con el detalle fino 🔍.
									</Typography>
								</>
							}
						/>
					</Box>
					<Stack alignItems="flex-end" spacing={0.5}>
						<CachedBadge cachedAt={convCachedAt} />
						<Box display="flex" gap={1}>
							<TextField
								label="Desde"
								type="date"
								size="small"
								value={dateFrom}
								onChange={(e) => setDateFrom(e.target.value)}
								InputLabelProps={{ shrink: true }}
								sx={{ width: 145 }}
							/>
							<TextField
								label="Hasta"
								type="date"
								size="small"
								value={dateTo}
								onChange={(e) => setDateTo(e.target.value)}
								InputLabelProps={{ shrink: true }}
								sx={{ width: 145 }}
							/>
						</Box>
					</Stack>
				</Box>
				{convError && (
					<Alert severity="error" sx={{ mb: 2 }}>
						{convError}
					</Alert>
				)}
				<Grid container spacing={2} mb={3}>
					{[
						{
							label: 'Casos asignados',
							value: conv?.cases_created ?? null,
							tooltip:
								'Casos del CRM creados en el período asignados al vendedor/equipo (excluye soporte y workflows excluidos). Período medio-abierto: incluye "Desde" y excluye "Hasta".',
						},
						{
							label: 'Casos convertidos',
							value: conv?.cases_converted ?? null,
							tooltip: 'De los casos asignados en el período, cuántos terminaron en una venta (match por dealId).',
						},
						{
							label: 'Tasa',
							value: conv ? `${Number(conv.conversion_pct).toFixed(1)}%` : null,
							tooltip: isTeam
								? 'Tasa real del equipo: total convertidos ÷ total asignados × 100.'
								: 'Porcentaje de casos asignados que terminaron en venta. Fórmula: convertidos ÷ asignados × 100.',
						},
						{
							label: 'Días prom.',
							value: conv?.avg_days_to_convert != null ? Number(conv.avg_days_to_convert).toFixed(1) : null,
							tooltip: 'Promedio de días entre creación del caso y primera venta, solo sobre casos que convirtieron.',
						},
						{
							label: 'Días mediana',
							value: conv?.median_days_to_convert != null ? Number(conv.median_days_to_convert).toFixed(1) : null,
							tooltip: 'Mediana de días entre creación del caso y primera venta. Más robusta ante outliers.',
						},
					].map((c) => (
						<Grid item xs={6} sm key={c.label}>
							<KpiCard label={c.label} value={c.value} loading={convLoading} tooltip={c.tooltip} />
						</Grid>
					))}
				</Grid>

				{/* Sentinel de lazy loading: al entrar en viewport dispara las secciones de abajo */}
				<div ref={belowFoldRef} aria-hidden style={{ height: 1 }} />

				{/* ── Ranking (solo equipo) ────────────────────────── */}
				{isTeam && (
					<>
						<Divider sx={{ mb: 3 }} />
						<Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
							<Box display="inline-flex" alignItems="center" gap={0.5}>
								<Typography variant="subtitle1" fontWeight={600}>
									Ranking del equipo (MTD)
								</Typography>
								<SectionInfoTooltip
									content={
										<>
											<Typography variant="body2" fontWeight={600} gutterBottom>
												¿Qué hay acá? 👀
											</Typography>
											<Typography variant="body2" gutterBottom>
												La tabla de posiciones del mes 🏁: quién viene liderando en margen, venta y órdenes.
											</Typography>
											<Typography variant="body2" gutterBottom>
												Ordenado por <strong>margen MTD</strong> 🥇 — porque acá lo que importa es lo que queda, no solo
												lo que entra.
											</Typography>
											<Typography variant="body2">Tocá una fila para entrar al dashboard de ese vendedor 👤</Typography>
										</>
									}
								/>
							</Box>
							<CachedBadge cachedAt={leaderboard.cachedAt} />
						</Box>
						{leaderboard.error && (
							<Alert severity="error" sx={{ mb: 2 }}>
								{leaderboard.error}
							</Alert>
						)}
						{leaderboard.loading ? (
							<Skeleton variant="rectangular" height={120} sx={{ mb: 3 }} />
						) : (
							<TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
								<Table size="small">
									<TableHead>
										<TableRow>
											<TableCell>#</TableCell>
											<TableCell>Vendedor</TableCell>
											<TableCell align="right">Margen MTD</TableCell>
											<TableCell align="right">Venta MTD</TableCell>
											<TableCell align="right">Órdenes</TableCell>
											<TableCell align="right">% del equipo</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{leaderboard.data && leaderboard.data.length > 0 ? (
											leaderboard.data.map((item) => (
												<TableRow
													key={item.seller_id}
													hover
													sx={{ cursor: 'pointer' }}
													onClick={() => history.push(routes.salesDashboard.seller(item.seller_name))}
												>
													<TableCell>{item.rank_in_team}</TableCell>
													<TableCell>{item.seller_name}</TableCell>
													<TableCell align="right">{formatUSD(Number(item.gp_mtd))}</TableCell>
													<TableCell align="right">{formatUSD(Number(item.sale_mtd))}</TableCell>
													<TableCell align="right">{item.orders_touched}</TableCell>
													<TableCell align="right">{Number(item.pct_of_team_gp).toFixed(1)}%</TableCell>
												</TableRow>
											))
										) : (
											<TableRow>
												<TableCell colSpan={6} align="center" sx={{ color: 'text.secondary' }}>
													Sin datos de leaderboard
												</TableCell>
											</TableRow>
										)}
									</TableBody>
								</Table>
							</TableContainer>
						)}
					</>
				)}

				{/* ── Mix por segmento ─────────────────────────────── */}
				<Divider sx={{ mb: 3 }} />
				<Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
					<Box display="inline-flex" alignItems="center" gap={0.5}>
						<Typography variant="subtitle1" fontWeight={600}>
							Mix por segmento y producto
						</Typography>
						<SectionInfoTooltip
							content={
								<>
									<Typography variant="body2" fontWeight={600} gutterBottom>
										¿Qué hay acá? 👀
									</Typography>
									<Typography variant="body2" gutterBottom>
										De qué está hecha tu venta 🧩: cuánto aporta cada segmento/producto al total del período.
									</Typography>
									<Typography variant="body2" gutterBottom>
										Cada barra muestra su peso en % y abajo la venta y el margen (GP) que dejó 💰.
									</Typography>
									<Typography variant="body2">
										Útil para ver si estás muy jugado a un solo producto 🎰 o tenés el mix balanceado ⚖️
									</Typography>
								</>
							}
						/>
					</Box>
					<CachedBadge cachedAt={productionMix.cachedAt} />
				</Box>
				{productionMix.error && (
					<Alert severity="error" sx={{ mb: 2 }}>
						{productionMix.error}
					</Alert>
				)}
				{productionMix.loading ? (
					<Skeleton variant="rectangular" height={80} sx={{ mb: 3 }} />
				) : displayMixData && displayMixData.length > 0 ? (
					<Grid container spacing={2} mb={3}>
						{displayMixData.map((seg, idx) => {
							const segment = seg.segmentType ?? '—';
							const service = seg.serviceType ?? '';
							const serviceMeta = service ? serviceTypeToEmoji[service] : undefined;
							const productLabel = serviceMeta ? `${serviceMeta.emoji} ${serviceMeta.name}` : service;
							const pct = mixTotal > 0 ? (Number(seg.sale_amount) / mixTotal) * 100 : 0;
							const color = segmentColor[segment] ?? segmentColor.Other;
							return (
								<Grid item xs={12} sm={6} md={4} key={`${idx}-${segment}-${service}`}>
									<Paper variant="outlined" sx={{ p: 1.5 }}>
										<Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5} gap={1}>
											<Box display="flex" alignItems="center" gap={0.75} minWidth={0}>
												<Chip
													label={segment}
													size="small"
													sx={{
														bgcolor: color,
														color: '#fff',
														fontWeight: 700,
														height: 20,
														'& .MuiChip-label': { px: 1 },
													}}
												/>
												{productLabel && (
													<Typography variant="body2" noWrap title={productLabel}>
														{productLabel}
													</Typography>
												)}
											</Box>
											<Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
												{pct.toFixed(1)}%
											</Typography>
										</Box>
										<Box sx={{ height: 6, borderRadius: 3, bgcolor: 'action.hover', overflow: 'hidden', mb: 0.75 }}>
											<Box sx={{ width: `${pct}%`, height: '100%', bgcolor: color, borderRadius: 3 }} />
										</Box>
										<Box display="flex" justifyContent="space-between">
											<Typography variant="body2">{formatUSD(Number(seg.sale_amount))}</Typography>
											<Typography variant="body2" color="text.secondary">
												GP: {formatUSD(Number(seg.gross_profit))}
											</Typography>
										</Box>
									</Paper>
								</Grid>
							);
						})}
					</Grid>
				) : (
					<Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
						Sin datos de mix en el período
					</Typography>
				)}

				{/* ── Cola de seguimiento ──────────────────────────── */}
				<Divider sx={{ mb: 3 }} />
				<Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
					<Box display="inline-flex" alignItems="center" gap={0.5}>
						<Typography variant="subtitle1" fontWeight={600}>
							Cola de seguimiento
						</Typography>
						<SectionInfoTooltip
							content={
								<>
									<Typography variant="body2" fontWeight={600} gutterBottom>
										🔥 Oportunidades abiertas sin cerrar
									</Typography>
									<Typography variant="body2" gutterBottom>
										Acá está todo lo que todavía tenés para convertir en venta. Nada de ruido: solo lo que está en
										juego. 🎯
									</Typography>
									<Typography variant="body2" fontWeight={600} gutterBottom>
										✅ Entran los casos que:
									</Typography>
									<Typography variant="body2" component="ul" sx={{ pl: 2, mb: 1 }}>
										<li>están en una etapa de venta activa (seguimiento, negociación, atención…), y</li>
										<li>todavía no tienen una venta registrada.</li>
									</Typography>
									<Typography variant="body2" fontWeight={600} gutterBottom>
										🚫 No vas a ver:
									</Typography>
									<Typography variant="body2" component="ul" sx={{ pl: 2, mb: 1 }}>
										<li>los que ya vendiste 🎉 (apenas se registra la venta, chau, se van solos),</li>
										<li>los cerrados como no vendidos 😔,</li>
										<li>y los casos operativos/internos que no son de venta 🛠️</li>
									</Typography>
									<Typography variant="body2">
										Van del más viejo al más nuevo ⏳ — arrancá por arriba, que esos son los que más te están pidiendo
										pista ✈️
									</Typography>
								</>
							}
						/>
					</Box>
					<CachedBadge cachedAt={pendingConversion.cachedAt} />
				</Box>
				<Box display="flex" gap={1} mb={1.5} flexWrap="wrap">
					<Chip
						label={`Todos (${pendingBucketsSummary.reduce((s, b) => s + b.count, 0).toLocaleString('es-AR')})`}
						size="small"
						variant={pendingBucket === undefined ? 'filled' : 'outlined'}
						color={pendingBucket === undefined ? 'primary' : 'default'}
						onClick={() => {
							setPendingBucket(undefined);
							setPendingOffset(0);
						}}
						sx={{ cursor: 'pointer' }}
					/>
					{pendingBucketsSummary.map((b) => (
						<Chip
							key={b.bucket}
							label={`${b.label}: ${b.count.toLocaleString('es-AR')}`}
							size="small"
							variant={pendingBucket === b.bucket ? 'filled' : 'outlined'}
							color={
								pendingBucket === b.bucket
									? b.bucket === '10+'
										? 'error'
										: b.bucket === '4-10'
										? 'warning'
										: 'success'
									: 'default'
							}
							onClick={() => {
								setPendingBucket(b.bucket);
								setPendingOffset(0);
							}}
							sx={{ cursor: 'pointer' }}
						/>
					))}
				</Box>
				{pendingConversion.error && (
					<Alert severity="error" sx={{ mb: 2 }}>
						{pendingConversion.error}
					</Alert>
				)}
				{pendingConversion.loading && pendingOffset === 0 ? (
					<Skeleton variant="rectangular" height={120} sx={{ mb: 3 }} />
				) : (
					<>
						<TableContainer component={Paper} variant="outlined" sx={{ mb: 1, maxHeight: 340, overflow: 'auto' }}>
							<Table size="small" stickyHeader>
								<TableHead>
									<TableRow>
										<TableCell sx={{ maxWidth: 200 }}>Caso</TableCell>
										<TableCell>Cliente</TableCell>
										{isTeam && <TableCell>Vendedor</TableCell>}
										<TableCell>Step</TableCell>
										{!pendingBucket && <TableCell>Tramo</TableCell>}
										<TableCell>Antigüedad</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{pendingCases.length > 0 ? (
										pendingCases.map((item) => (
											<TableRow key={item.case_id} hover>
												<TableCell sx={{ maxWidth: 200 }}>
													<Tooltip title={item.name} placement="top">
														<a
															href={`https://crm.mevuelo.com/dashboard/cases/${item.case_id}`}
															target="_blank"
															rel="noopener noreferrer"
															style={{
																color: 'inherit',
																display: 'inline-flex',
																alignItems: 'center',
																gap: 4,
																maxWidth: '100%',
															}}
														>
															<Box
																component="span"
																sx={{
																	overflow: 'hidden',
																	textOverflow: 'ellipsis',
																	whiteSpace: 'nowrap',
																	display: 'block',
																}}
															>
																{item.name}
															</Box>
															<OpenInNewIcon sx={{ fontSize: 12, opacity: 0.6, flexShrink: 0 }} />
														</a>
													</Tooltip>
												</TableCell>
												<TableCell>
													<Typography variant="body2" color={item.client_name ? 'text.primary' : 'text.disabled'}>
														{item.client_name ?? '—'}
													</Typography>
												</TableCell>
												{isTeam && <TableCell>{item.seller_name}</TableCell>}
												<TableCell>{item.step_name}</TableCell>
												{!pendingBucket && (
													<TableCell>
														<Chip
															label={item.bucket}
															size="small"
															color={item.bucket === '10+' ? 'error' : item.bucket === '4-10' ? 'warning' : 'success'}
															variant="outlined"
															sx={{ fontSize: 11 }}
														/>
													</TableCell>
												)}
												<TableCell>
													<AgeIndicator days={item.age_days} />
												</TableCell>
											</TableRow>
										))
									) : (
										<TableRow>
											<TableCell
												colSpan={isTeam ? (pendingBucket ? 5 : 6) : pendingBucket ? 4 : 5}
												align="center"
												sx={{ color: 'text.secondary' }}
											>
												Sin casos pendientes
											</TableCell>
										</TableRow>
									)}
								</TableBody>
							</Table>
						</TableContainer>
						{pendingPagination && pendingPagination.total > pendingOffset + pendingPagination.returned && (
							<Box display="flex" justifyContent="center" mb={3}>
								<Chip
									label={
										pendingConversion.loading
											? 'Cargando…'
											: `Cargar más (${pendingOffset + pendingPagination.returned} de ${pendingPagination.total})`
									}
									onClick={() => {
										if (!pendingConversion.loading) setPendingOffset((o) => o + 50);
									}}
									variant="outlined"
									size="small"
									sx={{ cursor: 'pointer' }}
								/>
							</Box>
						)}
						{(!pendingPagination || pendingPagination.total <= pendingOffset + (pendingPagination?.returned ?? 0)) &&
							pendingCases.length > 0 && <Box mb={3} />}
					</>
				)}

				{/* ── Necesidades operativas abiertas ─────────────── */}
				<OperationalNeedsWidget sellerId={scopeSellerId} team={scopeTeam} enabled={belowFoldInView} />

				{/* ── Saldos pendientes ────────────────────────────── */}
				<Divider sx={{ mb: 3 }} />
				<Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
					<Box display="inline-flex" alignItems="center" gap={0.5}>
						<Typography variant="subtitle1" fontWeight={600}>
							Saldos pendientes
						</Typography>
						<SectionInfoTooltip
							content={
								<>
									<Typography variant="body2" fontWeight={600} gutterBottom>
										¿Qué hay acá? 👀
									</Typography>
									<Typography variant="body2" gutterBottom>
										Plata que todavía no entró 💸: órdenes con saldo por cobrar al pasajero.
									</Typography>
									<Typography variant="body2" fontWeight={600} gutterBottom>
										La severidad te marca la urgencia 🚦:
									</Typography>
									<Typography variant="body2" component="ul" sx={{ pl: 2, mb: 1 }}>
										<li>
											🔴 <strong>Grave</strong> → ya viajó y sigue debiendo. Prioridad máxima.
										</li>
										<li>
											🟠 <strong>Vencida</strong> → viaja en menos de 45 días. Hay que moverse.
										</li>
										<li>
											🟢 <strong>OK</strong> → hay tiempo, pero no te duermas 😴
										</li>
									</Typography>
									<Typography variant="body2">Podés filtrar por segmento con los chips 🏷️</Typography>
								</>
							}
						/>
					</Box>
					<CachedBadge cachedAt={isTeam ? pendingBalanceSummary.cachedAt : pendingBalance.cachedAt} />
				</Box>
				{pendingBalanceSummary.error && (
					<Alert severity="error" sx={{ mb: 2 }}>
						{pendingBalanceSummary.error}
					</Alert>
				)}
				{isTeam && balanceSummary && (
					<Grid container spacing={2} mb={2}>
						{[
							{ label: 'Órdenes con saldo', value: String(balanceSummary.orders_with_balance), color: undefined },
							{
								label: 'Total pendiente',
								value: formatUSD(Number(balanceSummary.total_pending), 2),
								color: 'error.main',
							},
							{
								label: 'Grave (ya viajó)',
								value: `${balanceSummary.grave_count} · ${formatUSD(Number(balanceSummary.grave_amount), 2)}`,
								color: 'error.main',
							},
							{
								label: 'Vencida (<45d)',
								value: `${balanceSummary.vencida_count} · ${formatUSD(Number(balanceSummary.vencida_amount), 2)}`,
								color: 'warning.main',
							},
							{
								label: 'OK',
								value: `${balanceSummary.ok_count} · ${formatUSD(Number(balanceSummary.ok_amount), 2)}`,
								color: 'success.main',
							},
						].map((c) => (
							<Grid item xs={6} sm={4} md key={c.label}>
								<Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center' }}>
									<Typography
										variant="caption"
										color="text.secondary"
										sx={{ textTransform: 'uppercase', fontWeight: 600, display: 'block', mb: 0.25 }}
									>
										{c.label}
									</Typography>
									<Typography variant="body1" fontWeight={700} color={c.color ?? 'text.primary'}>
										{c.value}
									</Typography>
								</Paper>
							</Grid>
						))}
					</Grid>
				)}
				{pendingBalance.loading ? (
					<Skeleton variant="rectangular" height={120} sx={{ mb: 3 }} />
				) : pendingBalance.data && pendingBalance.data.length > 0 ? (
					<>
						{/* Chips de filtro por segmento */}
						{(() => {
							const segments = Array.from(
								new Set((pendingBalance.data ?? []).map((item) => item.segmentType ?? '—')),
							).sort();
							if (segments.length <= 1) return null;
							return (
								<Box display="flex" gap={0.75} flexWrap="wrap" mb={1.5}>
									<Chip
										label="Todos"
										size="small"
										color={balanceSegmentFilter === null ? 'primary' : 'default'}
										variant={balanceSegmentFilter === null ? 'filled' : 'outlined'}
										onClick={() => setBalanceSegmentFilter(null)}
										sx={{ cursor: 'pointer' }}
									/>
									{segments.map((seg) => (
										<Chip
											key={seg}
											label={seg}
											size="small"
											color={balanceSegmentFilter === seg ? 'primary' : 'default'}
											variant={balanceSegmentFilter === seg ? 'filled' : 'outlined'}
											onClick={() => setBalanceSegmentFilter(balanceSegmentFilter === seg ? null : seg)}
											sx={{ cursor: 'pointer' }}
										/>
									))}
								</Box>
							);
						})()}
						<TableContainer component={Paper} variant="outlined" sx={{ mb: 3, maxHeight: 320, overflow: 'auto' }}>
							<Table size="small" stickyHeader>
								<TableHead>
									<TableRow>
										<TableCell>Orden</TableCell>
										<TableCell>Salida</TableCell>
										{isTeam && <TableCell>Vendedor</TableCell>}
										<TableCell>Segmento</TableCell>
										<TableCell align="right">Saldo pend.</TableCell>
										<TableCell>Severidad</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{pendingBalance.data
										.filter((item) =>
											balanceSegmentFilter === null ? true : (item.segmentType ?? '—') === balanceSegmentFilter,
										)
										.map((item) => {
											const severityColor: Record<string, 'error' | 'warning' | 'success'> = {
												grave: 'error',
												vencida: 'warning',
												ok: 'success',
											};
											return (
												<TableRow key={item.sale_order_id} hover>
													<TableCell>
														<RouterLink
															to={`/salesorder/${item.sale_order_id}`}
															style={{ color: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 2 }}
														>
															{item.orderNumber ? `#${item.orderNumber}` : item.sale_order_id}
															<OpenInNewIcon sx={{ fontSize: 11, opacity: 0.5 }} />
														</RouterLink>
													</TableCell>
													<TableCell sx={{ whiteSpace: 'nowrap' }}>
														{item.travelDepartureDate ? formatDate(item.travelDepartureDate) : '—'}
													</TableCell>
													{isTeam && <TableCell>{item.seller_name}</TableCell>}
													<TableCell>{item.segmentType ?? '—'}</TableCell>
													<TableCell align="right">
														<Typography variant="body2" fontWeight={600} color={`${severityColor[item.severity]}.main`}>
															{formatUSD(Number(item.pending_amount), 2)}
														</Typography>
													</TableCell>
													<TableCell>
														<Chip
															label={item.severity}
															size="small"
															color={severityColor[item.severity]}
															variant="outlined"
															sx={{ fontSize: 10 }}
														/>
													</TableCell>
												</TableRow>
											);
										})}
								</TableBody>
							</Table>
						</TableContainer>
					</>
				) : (
					<Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
						Sin saldos pendientes
					</Typography>
				)}

				{/* ── Alertas críticas ─────────────────────────────── */}
				<Divider sx={{ mb: 3 }} />
				<Box display="inline-flex" alignItems="center" gap={0.5} mb={1.5}>
					<Typography variant="subtitle1" fontWeight={600}>
						Alertas críticas (viaje en ≤7 días)
					</Typography>
					<SectionInfoTooltip
						content={
							<>
								<Typography variant="body2" fontWeight={600} gutterBottom>
									¿Qué hay acá? 👀
								</Typography>
								<Typography variant="body2" gutterBottom>
									Incendios a apagar 🔥: reservas que salen de viaje en los próximos 7 días y que todavía tienen un cabo
									suelto.
								</Typography>
								<Typography variant="body2" fontWeight={600} gutterBottom>
									Una reserva entra si le falta al menos una de estas 👇
								</Typography>
								<Typography variant="body2" component="ul" sx={{ pl: 2, mb: 1 }}>
									<li>
										💰 <strong>Saldo por cobrar</strong> → el pasajero todavía debe plata.
									</li>
									<li>
										🎫 <strong>Emisión pendiente</strong> → el ticket no se emitió o no se cargó en el item.
									</li>
								</Typography>
								<Typography variant="body2">Ordenadas por fecha de viaje ⏱️ (lo más al horno, primero).</Typography>
							</>
						}
					/>
				</Box>
				{criticalAlerts.error && (
					<Alert severity="error" sx={{ mb: 2 }}>
						{criticalAlerts.error}
					</Alert>
				)}
				{criticalAlerts.loading ? (
					<Skeleton variant="rectangular" height={80} sx={{ mb: 3 }} />
				) : criticalAlerts.data && criticalAlerts.data.length > 0 ? (
					<TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
						<Table size="small">
							<TableHead>
								<TableRow>
									<TableCell>Orden</TableCell>
									{isTeam && <TableCell>Vendedor</TableCell>}
									<TableCell>Días para viaje</TableCell>
									<TableCell>Saldo pend.</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{criticalAlerts.data.map((item) => (
									<TableRow key={item.sale_order_id} hover>
										<TableCell>
											<RouterLink to={`/salesorder/${item.sale_order_id}`} style={{ color: 'inherit' }}>
												{item.sale_order_id}
											</RouterLink>
										</TableCell>
										{isTeam && <TableCell>{(item as any).seller_name ?? '—'}</TableCell>}
										<TableCell>
											<AgeIndicator days={item.days_to_travel} />
										</TableCell>
										<TableCell>{item.has_balance ? formatUSD(Number(item.pending_amount)) : '—'}</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</TableContainer>
				) : (
					<Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
						Sin alertas críticas
					</Typography>
				)}

				{/* ── Emisiones pendientes ─────────────────────────── */}
				<Divider sx={{ mb: 3 }} />
				<Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
					<Box display="inline-flex" alignItems="center" gap={0.5}>
						<Typography variant="subtitle1" fontWeight={600}>
							Emisiones pendientes
						</Typography>
						<SectionInfoTooltip
							content={
								<>
									<Typography variant="body2" fontWeight={600} gutterBottom>
										¿Qué hay acá? 👀
									</Typography>
									<Typography variant="body2" gutterBottom>
										Tickets que todavía no se emitieron 🎫 — y las reservas no esperan para siempre.
									</Typography>
									<Typography variant="body2" gutterBottom>
										Ordenadas por <strong>límite de emisión</strong> ⏰: lo que vence primero, arriba.
									</Typography>
									<Typography variant="body2">
										Si ves el chip 🔴 <strong>&lt; 24h</strong>, ese ticket está al borde del precipicio — resolvelo YA
										o la reserva se cae 🪂
									</Typography>
								</>
							}
						/>
					</Box>
					<CachedBadge cachedAt={pendingIssues.cachedAt} />
				</Box>
				{pendingIssues.error && (
					<Alert severity="error" sx={{ mb: 2 }}>
						{pendingIssues.error}
					</Alert>
				)}
				{pendingIssues.loading ? (
					<Skeleton variant="rectangular" height={100} sx={{ mb: 3 }} />
				) : pendingIssues.data && pendingIssues.data.length > 0 ? (
					<TableContainer component={Paper} variant="outlined" sx={{ mb: 3, maxHeight: 320, overflow: 'auto' }}>
						<Table size="small" stickyHeader>
							<TableHead>
								<TableRow>
									<TableCell>Orden</TableCell>
									<TableCell>Pasajero</TableCell>
									{isTeam && <TableCell>Vendedor</TableCell>}
									<TableCell>GDS / VC</TableCell>
									<TableCell>Tipo</TableCell>
									<TableCell>Límite emisión</TableCell>
									<TableCell>Salida</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{[...pendingIssues.data]
									.sort((a, b) => {
										if (!a.ticketingLimitDatetime) return 1;
										if (!b.ticketingLimitDatetime) return -1;
										return new Date(a.ticketingLimitDatetime).getTime() - new Date(b.ticketingLimitDatetime).getTime();
									})
									.map((item, idx) => {
										const orderId = item.saleOrderId ?? item.sale_order_id ?? '';
										const isUrgent = item.limit_within_24h;
										return (
											<TableRow key={`${orderId}-${idx}`} hover sx={{ bgcolor: isUrgent ? 'error.50' : undefined }}>
												<TableCell>
													<RouterLink
														to={`/salesorder/${orderId}${item.serviceId ? `?serviceId=${item.serviceId}` : ''}`}
														style={{ color: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 2 }}
													>
														{orderId}
														<OpenInNewIcon sx={{ fontSize: 11, opacity: 0.5 }} />
													</RouterLink>
												</TableCell>
												<TableCell>{item.mainPassengerCompleteName ?? '—'}</TableCell>
												{isTeam && <TableCell>{item.seller_name ?? '—'}</TableCell>}
												<TableCell>{[item.gds, item.vcCode].filter(Boolean).join(' / ') || '—'}</TableCell>
												<TableCell>{item.ticketType ?? '—'}</TableCell>
												<TableCell sx={{ whiteSpace: 'nowrap' }}>
													{item.ticketingLimitDatetime ? (
														<Box display="inline-flex" alignItems="center" gap={0.5}>
															{new Date(item.ticketingLimitDatetime).toLocaleString('es', {
																day: '2-digit',
																month: '2-digit',
																hour: '2-digit',
																minute: '2-digit',
															})}
															{isUrgent && (
																<Chip label="< 24h" size="small" color="error" sx={{ fontSize: 10, height: 18 }} />
															)}
														</Box>
													) : (
														'—'
													)}
												</TableCell>
												<TableCell sx={{ whiteSpace: 'nowrap' }}>
													{item.saleOrderTravelDepartureDate ? formatDate(item.saleOrderTravelDepartureDate) : '—'}
												</TableCell>
											</TableRow>
										);
									})}
							</TableBody>
						</Table>
					</TableContainer>
				) : (
					<Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
						Sin emisiones pendientes
					</Typography>
				)}

				{/* ── Viajes próximos ──────────────────────────────── */}
				<Divider sx={{ mb: 3 }} />
				<Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
					<Box display="inline-flex" alignItems="center" gap={0.5}>
						<Typography variant="subtitle1" fontWeight={600}>
							Viajes próximos (2 semanas)
						</Typography>
						<SectionInfoTooltip
							content={
								<>
									<Typography variant="body2" fontWeight={600} gutterBottom>
										¿Qué hay acá? 👀
									</Typography>
									<Typography variant="body2" gutterBottom>
										Los pasajeros que se van de viaje en los próximos 14 días 🧳✈️
									</Typography>
									<Typography variant="body2" gutterBottom>
										Es tu radar para el último check ✅: que no quede saldo pendiente 💰 ni ningún detalle suelto antes
										de que suban al avión.
									</Typography>
									<Typography variant="body2">
										Si tiene saldo pendiente, lo vas a ver marcado — cobrar antes del embarque es mil veces más fácil
										que después 😅
									</Typography>
								</>
							}
						/>
					</Box>
					<CachedBadge cachedAt={upcomingTravels.cachedAt} />
				</Box>
				{upcomingTravels.error && (
					<Alert severity="error" sx={{ mb: 2 }}>
						{upcomingTravels.error}
					</Alert>
				)}
				{upcomingTravels.loading ? (
					<Skeleton variant="rectangular" height={120} sx={{ mb: 3 }} />
				) : (
					<TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
						<Table size="small">
							<TableHead>
								<TableRow>
									<TableCell>Orden</TableCell>
									<TableCell>Salida</TableCell>
									<TableCell>Destino</TableCell>
									<TableCell>Pasajero principal</TableCell>
									{isTeam && <TableCell>Vendedor</TableCell>}
									<TableCell>Pax</TableCell>
									<TableCell>Saldo pend.</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{sortedTravels.length > 0 ? (
									sortedTravels.map((item) => {
										const pendingAmt = Number(item.pending_amount);
										const hasPending = item.has_pending_balance && pendingAmt > 0;
										return (
											<TableRow key={item.sale_order_id} hover>
												<TableCell>
													<Box display="inline-flex" alignItems="center" gap={0.5}>
														<RouterLink
															to={`/salesorder/${item.sale_order_id}`}
															style={{ color: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 2 }}
														>
															#{item.orderNumber}
															<OpenInNewIcon sx={{ fontSize: 12, opacity: 0.5 }} />
														</RouterLink>
														{item.case_id && (
															<Tooltip title="Ver caso en CRM">
																<a
																	href={`https://crm.mevuelo.com/dashboard/cases/${item.case_id}`}
																	target="_blank"
																	rel="noopener noreferrer"
																	style={{ color: 'inherit', display: 'inline-flex', alignItems: 'center' }}
																>
																	<OpenInNewIcon sx={{ fontSize: 12, opacity: 0.5, color: 'secondary.main' }} />
																</a>
															</Tooltip>
														)}
													</Box>
												</TableCell>
												<TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(item.travelDepartureDate)}</TableCell>
												<TableCell>
													{item.destinations ?? (
														<Typography variant="body2" color="text.disabled">
															—
														</Typography>
													)}
												</TableCell>
												<TableCell>{item.main_passenger}</TableCell>
												{isTeam && <TableCell>{item.seller_name}</TableCell>}
												<TableCell align="center">{item.pax_count}</TableCell>
												<TableCell>
													{hasPending ? (
														<Typography variant="body2" color="error.main" fontWeight={600}>
															{formatUSD(pendingAmt)}
														</Typography>
													) : (
														<Typography variant="body2" color="success.main">
															Sin saldo
														</Typography>
													)}
												</TableCell>
											</TableRow>
										);
									})
								) : (
									<TableRow>
										<TableCell colSpan={isTeam ? 7 : 6} align="center" sx={{ color: 'text.secondary' }}>
											Sin viajes próximos
										</TableCell>
									</TableRow>
								)}
							</TableBody>
						</Table>
					</TableContainer>
				)}

				{/* ── Variaciones de margen ────────────────────────── */}
				<Divider sx={{ mb: 3 }} />
				<Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5} flexWrap="wrap" gap={1}>
					<Box display="inline-flex" alignItems="center" gap={0.5}>
						<Typography variant="subtitle1" fontWeight={600}>
							Variaciones de margen
						</Typography>
						<SectionInfoTooltip
							content={
								<>
									<Typography variant="body2" fontWeight={600} gutterBottom>
										¿Qué hay acá? 👀
									</Typography>
									<Typography variant="body2" gutterBottom>
										Órdenes donde la venta o el margen cambiaron después de registradas 🔄 (recotizaciones, ajustes de
										costo, cambios del proveedor…).
									</Typography>
									<Typography variant="body2" component="ul" sx={{ pl: 2, mb: 1 }}>
										<li>🟢 Verde → el margen mejoró. Bien ahí 👏</li>
										<li>🔴 Rojo → el margen se achicó. Conviene mirar qué pasó 🕵️</li>
									</Typography>
									<Typography variant="body2">
										Con el switch <strong>&quot;Solo negativas&quot;</strong> filtrás directo lo que duele, y con el
										umbral definís desde cuánto te importa 💵
									</Typography>
								</>
							}
						/>
					</Box>
					<Box display="flex" gap={1.5} alignItems="center" flexWrap="wrap">
						<FormControlLabel
							control={
								<Switch checked={onlyNegative} onChange={(e) => setOnlyNegative(e.target.checked)} size="small" />
							}
							label={<Typography variant="caption">Solo negativas</Typography>}
							sx={{ m: 0 }}
						/>
						{onlyNegative && (
							<TextField
								label="Umbral mín."
								type="number"
								size="small"
								value={threshold}
								onChange={(e) => setThreshold(Math.max(0, Number(e.target.value)))}
								InputProps={{ startAdornment: <InputAdornment position="start">USD</InputAdornment> }}
								sx={{ width: 130 }}
							/>
						)}
						<CachedBadge cachedAt={variations.cachedAt} />
					</Box>
				</Box>
				{variations.error && (
					<Alert severity="error" sx={{ mb: 2 }}>
						{variations.error}
					</Alert>
				)}
				{variations.loading ? (
					<Skeleton variant="rectangular" height={120} sx={{ mb: 3 }} />
				) : (
					<TableContainer component={Paper} variant="outlined" sx={{ mb: 3, maxHeight: 380, overflow: 'auto' }}>
						<Table size="small" stickyHeader>
							<TableHead>
								<TableRow>
									<TableCell>Fecha</TableCell>
									<TableCell>Orden</TableCell>
									<TableCell>Destino</TableCell>
									{isTeam && <TableCell>Vendedor</TableCell>}
									<TableCell>Segmento</TableCell>
									<TableCell align="right">Δ Venta</TableCell>
									<TableCell align="right">Δ Margen</TableCell>
									<TableCell align="right">Margen resultante</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{variations.data && variations.data.length > 0 ? (
									variations.data.map((item, idx) => (
										<TableRow key={`${item.sale_order_id}-${idx}`} hover>
											<TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(item.dateCreated)}</TableCell>
											<TableCell>
												<RouterLink
													to={`/salesorder/${item.sale_order_id}`}
													style={{ color: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 2 }}
												>
													#{item.orderNumber}
													<OpenInNewIcon sx={{ fontSize: 11, opacity: 0.5 }} />
												</RouterLink>
											</TableCell>
											<TableCell>{item.destinations ?? '—'}</TableCell>
											{isTeam && <TableCell>{item.seller_name}</TableCell>}
											<TableCell>{item.segmentType}</TableCell>
											<TableCell align="right">
												<Typography
													variant="body2"
													color={item.sale_variation < 0 ? 'error.main' : 'success.main'}
													fontWeight={600}
												>
													{formatUSD(item.sale_variation)}
												</Typography>
											</TableCell>
											<TableCell align="right">
												<Typography
													variant="body2"
													color={item.gp_variation < 0 ? 'error.main' : 'success.main'}
													fontWeight={600}
												>
													{formatUSD(item.gp_variation)}
												</Typography>
											</TableCell>
											<TableCell align="right">{formatUSD(item.gp_after)}</TableCell>
										</TableRow>
									))
								) : (
									<TableRow>
										<TableCell colSpan={isTeam ? 8 : 7} align="center" sx={{ color: 'text.secondary' }}>
											Sin variaciones en el período
										</TableCell>
									</TableRow>
								)}
							</TableBody>
						</Table>
					</TableContainer>
				)}
			</Container>
		</Layout>
	);
}

// ─── Route wrappers con guards de acceso ──────────────────────────────────────

/**
 * `/dashboard/:sellerSlug`
 * - SELLER: solo puede ver su propio slug.
 * - ADMIN: puede ver su propio slug + cualquier vendedor de su equipo.
 * - SUPERADMIN: puede ver cualquier vendedor.
 */
export function SellerDashboardRoute(): JSX.Element {
	const { sellerSlug } = useParams<{ sellerSlug: string }>();
	const user = useSelector((state: RootState) => state.auth.user, shallowEqual);
	const { team, loading: teamLoading } = useCurrentUserTeam();
	// Superadmin resuelve contra el leaderboard global (puede ver cualquier vendedor).
	// Admin resuelve contra su propio equipo (solo puede ver los suyos).
	const leaderboard = useLeaderboard({ team: isSuperAdmin(user) ? undefined : team ?? undefined });

	const ownSlug = user?.name ? slugify(user.name) : '';

	// Resolver slug → sellerId + nombre + team desde el leaderboard
	const resolved = useMemo(() => {
		if (!leaderboard.data) return null;
		const match = leaderboard.data.find((s) => slugify(s.seller_name) === sellerSlug);
		if (match) return { sellerId: Number(match.seller_id), sellerName: match.seller_name, team: match.team };
		return null;
	}, [leaderboard.data, sellerSlug]);

	const isOwn = sellerSlug === ownSlug;

	// Mientras carga team o leaderboard (para admin+) mostrar spinner
	if (teamLoading || (isAdminOrSuperAdmin(user) && leaderboard.loading)) {
		return (
			<Layout>
				<Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
					<CircularProgress />
				</Box>
			</Layout>
		);
	}

	// SELLER: solo puede ver su propio dashboard
	if (!isAdminOrSuperAdmin(user)) {
		if (!isOwn) {
			return <Redirect to={ownSlug ? routes.salesDashboard.seller(user?.name ?? '') : '/'} />;
		}
		return <SalesDashboard scope={{ type: 'seller', sellerId: user?.id ?? 0, sellerName: user?.name ?? '' }} />;
	}

	// ADMIN/SUPERADMIN: verificar que el vendedor existe en el leaderboard del equipo
	if (isOwn) {
		return (
			<SalesDashboard
				scope={{
					type: 'seller',
					sellerId: user?.id ?? 0,
					sellerName: user?.name ?? '',
					contextTeam: team ?? undefined,
				}}
			/>
		);
	}

	if (resolved) {
		return (
			<SalesDashboard
				scope={{
					type: 'seller',
					sellerId: resolved.sellerId,
					sellerName: resolved.sellerName,
					contextTeam: resolved.team ?? team ?? undefined,
				}}
			/>
		);
	}

	// No se pudo resolver el vendedor. Solo redirigimos al equipo si es un equipo
	// de ventas válido; si no, a Home (evita loop con equipos como "DIRECCION").
	return <Redirect to={isSalesTeam(team) ? routes.salesDashboard.team(team) : '/'} />;
}

/**
 * `/dashboard/equipo/:teamSlug`
 * - SELLER: no tiene acceso — redirige a su propio dashboard.
 * - ADMIN: solo puede ver su equipo.
 * - SUPERADMIN: puede ver cualquier equipo de la lista SALES_TEAMS.
 */
export function TeamDashboardRoute(): JSX.Element {
	const { teamSlug } = useParams<{ teamSlug: string }>();
	const user = useSelector((state: RootState) => state.auth.user, shallowEqual);
	const { team: userTeam, loading: teamLoading } = useCurrentUserTeam();

	const resolvedTeam = teamFromSlug(teamSlug);

	if (teamLoading) {
		return (
			<Layout>
				<Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
					<CircularProgress />
				</Box>
			</Layout>
		);
	}

	// Sellers no tienen acceso a la vista de equipo
	if (!isAdminOrSuperAdmin(user)) {
		return <Redirect to={user?.name ? routes.salesDashboard.seller(user.name) : '/'} />;
	}

	// Team slug inválido (no existe en SALES_TEAMS) → a su equipo si es válido, si no Home
	if (!resolvedTeam) {
		return <Redirect to={isSalesTeam(userTeam) ? routes.salesDashboard.team(userTeam) : '/'} />;
	}

	// ADMIN: solo puede ver su propio equipo
	if (!isSuperAdmin(user)) {
		if (!userTeam || slugify(userTeam) !== teamSlug) {
			return <Redirect to={isSalesTeam(userTeam) ? routes.salesDashboard.team(userTeam) : '/'} />;
		}
	}

	return <SalesDashboard scope={{ type: 'team', team: resolvedTeam }} />;
}

// Re-export legacy redirects
export { SalesDashboard as default };
